import { useMemo } from 'react';

import { APPLE_PAY, PAYMENT_ADD } from '@order/constants';
import type { PaymentMethod } from '@order/graphql';
import { useLocalizationContext } from '@order/Localization';

import type { CreditCardOption } from '../../Checkout.utils';

export function useCreditCardOptions(
  paymentMethods: readonly PaymentMethod[],
  isApplePaySupported: boolean,
  isApplePayDefault: boolean,
) {
  const { t } = useLocalizationContext();
  const newCardLabel = t('checkout.add-card');
  const applePayLabel = t('general.apple-pay');

  const creditCardOptions: readonly CreditCardOption[] = useMemo(() => {
    if (
      isApplePaySupported &&
      (isApplePayDefault || paymentMethods.length > 0)
    ) {
      return [
        {
          value: PAYMENT_ADD,
          label: newCardLabel,
          cardType: PAYMENT_ADD,
          paymentMethodType: 'BillingAccount' as const,
          isDefault: false,
        },
        {
          label: applePayLabel,
          value: APPLE_PAY,
          cardType: APPLE_PAY,
          paymentMethodType: 'PaymentMethodCard' as const,
          isDefault: true,
        },
        ...(paymentMethods.map((paymentMethod) => ({
          value: paymentMethod.id,
          label: getPaymentMethodLabel(paymentMethod),
          cardType: paymentMethod.cardType,
          isDefault: paymentMethod.isDefault,
          paymentMethodType:
            paymentMethod.__typename ?? ('BillingAccount' as const),
        })) ?? []),
      ];
    }

    return [
      {
        value: PAYMENT_ADD,
        label: newCardLabel,
        cardType: PAYMENT_ADD,
        paymentMethodType: 'BillingAccount' as const,
        isDefault: false,
      },
      ...(paymentMethods.map((paymentMethod) => ({
        value: paymentMethod.id,
        label: getPaymentMethodLabel(paymentMethod),
        cardType: paymentMethod.cardType,
        isDefault: paymentMethod.isDefault,
        paymentMethodType:
          paymentMethod.__typename ?? ('BillingAccount' as const),
      })) ?? []),
    ];
  }, [
    isApplePaySupported,
    isApplePayDefault,
    paymentMethods,
    newCardLabel,
    applePayLabel,
  ]);

  return { creditCardOptions };
}

function getPaymentMethodLabel(paymentMethod: PaymentMethod) {
  if (paymentMethod.nickname) {
    return `${paymentMethod.nickname} - ${paymentMethod.description}`;
  }

  return paymentMethod.description ?? '';
}
